import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import SecondaryButton from 'components/buttons/SecondaryButton';
import PrimaryButton from 'components/buttons/PrimaryButton';
import { useGlobalContext } from 'GlobalContext';
import DateInput from 'components/forms/basefields/DateInput';
import TimeInput from 'components/forms/basefields/TimeInput';
import Dropdown from 'components/forms/basefields/Dropdown';
import { UserType } from '../users/UserTypes';
import { useSettings } from 'services/settings/SettingsContext';
import { convertLocalDateTimeToUTC } from 'internationalization/timezoneConversions';
import { combineDateAndTimeInputsToDatetimeString, formatTimelineDate } from 'services/utils/dateTimeUtils';
import { Task, TaskType } from './TaskTypes';
import FormFieldContext from 'components/forms/FormFieldContext';
import TabSelect from 'components/forms/basefields/TabSelect';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import TextInput from 'components/forms/basefields/TextInput';
import TextareaInput from 'components/forms/basefields/TextareaInput';
import { sanitizeTextareaFromString } from 'services/utils/convertTextareaData';
import 'react-quill/dist/quill.snow.css';
import '../../style/scss/forms.scss';
import { useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWrench } from '@fortawesome/free-solid-svg-icons';
import { useModal } from 'components/modals/ModalContext';

interface TaskFormFieldsProps {
    data: Task;
    placement: 'inline' | 'modal';
    taskTypes: TaskType[];
    assignees: UserType[];
    buttonLoader: boolean;
    handleClose: () => void;
    handleDelete: () => void;
    handleSubmit: () => void;
    setData: React.Dispatch<React.SetStateAction<Task | null>>;
}

const TaskFormFields: React.FC<TaskFormFieldsProps> = ({ 
    data, placement, assignees, taskTypes, buttonLoader, 
    handleClose, handleDelete, handleSubmit, setData
}) => {
    const { t } = useTranslation();
    const { userHash, allowedRights } = useAuthContext();
    const { userLocale, userTimezone } = useSettings();
    const { errorMessages, setErrorMessages } = useGlobalContext();
    const { closeAllModals } = useModal();
    const { setUpdatedData } = useContext(FormFieldContext);
    const history = useHistory();

    // Handle the date change
    const handleDateChange = (selectedDate: string | null, fieldName: 'date') => {
        
        // Copy the data to update it
        const updatedTask = { ...data };

        // Set the selected value in the field
        updatedTask[fieldName] = selectedDate;

        // Combine the selected date and time values into datetime fields
        let combinedDatetime, utcDatetime;
        if (selectedDate !== null) {

            // Handle datetime conversion when start date field is changed
            if (fieldName === 'date') {

                // If a start time exists, combine the start datetime
                if (data.start_time) {
                    // Combine the date input and time input into a local datetime string
                    combinedDatetime = combineDateAndTimeInputsToDatetimeString(selectedDate, data.start_time);

                    // Convert the local datetime into an utc datetime string for the backend
                    utcDatetime = convertLocalDateTimeToUTC(combinedDatetime, userLocale, userTimezone);

                    // Update the start datetime
                    updatedTask.start_datetime = utcDatetime;
                }
                
                // // If the start date and end date are equal, and end time exists, combine the start datetime
                // if ((data.end_date === null || (data.date === data.end_date)) && data.end_time) {

                //     // Set the end date to the selected date
                //     updatedTask.end_date = selectedDate;

                //     // Combine the date input and time input into a local datetime string
                //     combinedDatetime = combineDateAndTimeInputsToDatetimeString(selectedDate, data.end_time);

                //     // Convert the local datetime into an utc datetime string for the backend
                //     utcDatetime = convertLocalDateTimeToUTC(combinedDatetime, userLocale, userTimezone);

                //     // Update the end datetime
                //     updatedTask.end_datetime = utcDatetime;
                // }
            }

            // Handle datetime conversion when end date field is changed
            // if (fieldName === 'end_date' && data.end_time) {
            //     // Combine the date input and time input into a local datetime string
            //     combinedDatetime = combineDateAndTimeInputsToDatetimeString(selectedDate, data.end_time);

            //     // Convert the local datetime into an utc datetime string for the backend
            //     utcDatetime = convertLocalDateTimeToUTC(combinedDatetime, userLocale, userTimezone);

            //     // Update the end datetime
            //     updatedTask.end_datetime = utcDatetime;
            // }
        } else {
            // Handle in case the date is removed
            if (fieldName === 'date') {
                // // If the start date and end date are equal, remove both start- and end-datetimes
                // if (data.date && data.end_date && (data.date === data.end_date)) {
                //     updatedTask.start_datetime = null;
                //     updatedTask.end_datetime = null;

                // // Otherwise only remove the start-datetime
                // } else {
                    updatedTask.start_datetime = null;
                // }
            }

            // Handle in case the end date is removed
            // if (fieldName === 'end_date') {
            //     // If the start date still exists and end time, set the end date to the start date
            //     if (data.date && data.end_time) {
            //         // Combine the date input and time input into a local datetime string
            //         combinedDatetime = combineDateAndTimeInputsToDatetimeString(data.date, data.end_time);

            //         // Convert the local datetime into an utc datetime string for the backend
            //         utcDatetime = convertLocalDateTimeToUTC(combinedDatetime, userLocale, userTimezone);

            //         // Update the end datetime
            //         updatedTask.end_datetime = utcDatetime;

            //     // Otherwise remove the end-datetime
            //     } else {
            //         updatedTask.end_datetime = null;
            //     }
            // }
        }

        // Update the rows and updated data
        setData(updatedTask);
        setUpdatedData(updatedTask);
    }

    // Handle time change
    const handleTimeChange = (selectedTime: string | null, fieldName: 'start_time') => {
        
        // Copy the data to update it
        const updatedTask = { ...data };

        // Set the selected value in the field
        updatedTask[fieldName] = selectedTime;

        // Combine the time value to a datetime string if the start_date value exists
        let combinedDatetime, utcDatetime;
        if (selectedTime !== null && selectedTime !== '') {

            // Check if the start date exists
            if (data.date !== null) {

                // Handle if the end time changed and the end date differs from the start date
                // if (data.end_date !== null && (data.date !== data.end_date) && fieldName === 'end_time') {

                //     // Combine the date input and time input into a local datetime string
                //     combinedDatetime = combineDateAndTimeInputsToDatetimeString(data.end_date, selectedTime);

                //     // Convert the local datetime into an utc datetime string for the backend
                //     utcDatetime = convertLocalDateTimeToUTC(combinedDatetime, userLocale, userTimezone);

                //     // Update the end datetime
                //     updatedTask.end_datetime = utcDatetime;
                // }

                // Handle if both the end- or start-time changed, but the start- and end-dates are equal
                // if ((data.end_date !== null && (data.date === data.end_date)) || data.end_date === null) {

                    // Combine the date input and time input into a local datetime string
                    combinedDatetime = combineDateAndTimeInputsToDatetimeString(data.date, selectedTime);

                    // Convert the local datetime into an utc datetime string for the backend
                    utcDatetime = convertLocalDateTimeToUTC(combinedDatetime, userLocale, userTimezone);

                    // Update the start datetime
                    if (fieldName === 'start_time') {
                        updatedTask.start_datetime = utcDatetime;
                    }
        
                    // // Update the end datetime
                    // if (fieldName === 'end_time') {
                    //     updatedTask.end_datetime = utcDatetime;
                    // }    
                // }
            }
        
        } else {
            // If the selected time is set to null, remove the start- or end-datetimes
            if (fieldName === 'start_time') {
                updatedTask.start_datetime = null;
            }

            // // Update the end datetime
            // if (fieldName === 'end_time') {
            //     updatedTask.end_datetime = null;
            // }
        }
        
        // Update the data and updated data
        setData(updatedTask);
        setUpdatedData(updatedTask);
    }

    // Handle input change
    const handleInputChange = (value: string, fieldName: 'subject') => {
        setData((prev) => prev ? { ...prev, [fieldName]: value } : prev);
        setUpdatedData((prev: Task) => ({ ...prev, [fieldName]: value }));
    };

    // Handle textarea change
    const handleTextareaChange = (value: string | React.ChangeEvent<HTMLTextAreaElement>, fieldName: 'note') => {
        // Get the actual value
        const actualValue = typeof value === 'string' ? value : value.target.value;

        // Sanitize the textarea value
        const sanitizedValue = sanitizeTextareaFromString(actualValue);

        // Update the data and updated data
        setData((prev) => prev ? { ...prev, [fieldName]: sanitizedValue } : prev);
        setUpdatedData((prev: Task) => ({ ...prev, [fieldName]: sanitizedValue }));
    };

    // Handle the dropdown change
    const handleDropdownChange = (selectedValue: string, fieldName: 'assignee') => {
        setData((prev) => prev ? { ...prev, [fieldName]: parseInt(selectedValue) } : prev);
        setUpdatedData((prev: Task) => ({ ...prev, [fieldName]: parseInt(selectedValue) }));
    };

    // Handle the task type change
    const handleTaskTypeChange = (selectedValue: string) => {
        // Get the id of the new task type
        const newTaskTypeId = parseInt(selectedValue);

        // Get the current data
        const currentTaskData = data;
        if (!currentTaskData) return;

        // Get the new task type object
        const newTaskType = taskTypes.find(type => type.id === newTaskTypeId);

        // Update the task subject with the task type name if the subject is empty
        const shouldUpdateSubject = newTaskType && currentTaskData.subject === '';

        // Update the data
        setData((prev) => prev ? { 
            ...prev, 
            type: newTaskTypeId,
            ...(shouldUpdateSubject ? { subject: newTaskType.name } : {})
        } : prev);

        // Update the updated data
        setUpdatedData((prev: Task) => ({ 
            ...prev, 
            type: newTaskTypeId,
            ...(shouldUpdateSubject ? { subject: newTaskType.name } : {})
        }));
    };

    // Handle redirect to task type settings
    const handleRedirectToTaskTypeSettings = () => {

        // If the task form is opened within a modal, close all modals
        if (placement === 'modal') {
            closeAllModals();
        }

        // Redirect to the url
        history.push('/settings/custom-fields/categories');
    };

    // Determine to show the end date field
    // const showEndDateField = () => {
    //     let showEndDateField = false;

    //     // Only show the end date field if it differs from the start date field
    //     if (data.start_datetime && data.end_datetime) {
    //         const rowStartDateTime = extractDateFromLocalDateTime(data.start_datetime, userLocale);
    //         const rowEndDateTime = extractDateFromLocalDateTime(data.end_datetime, userLocale)

    //         showEndDateField = rowStartDateTime !== rowEndDateTime;
    //     };

    //     return showEndDateField;
    // };

    return (
        <div className='task-form'>
            <div>
                <label>
                    {t('task.general.subject_label')}
                </label>
                <TextInput
                    name='subject'
                    value={data.subject}
                    placeholder='task.general.subject_placeholder'
                    onChange={(value) => handleInputChange(value, 'subject')}
                    shouldAutoFocus={true}
                    isInvalid={errorMessages['subject']}
                />
                {errorMessages['subject'] && (
                    <div className='error-message'>
                        {t(errorMessages['subject'])}
                    </div>
                )}
            </div>
            <div>
                <div className='label-link-wrapper'>
                    <label>
                        {t('task.general.task_type_label')} 
                    </label>
                    {allowedRights.includes('change_settings') && (
                        <span className='text-link'
                              onClick={handleRedirectToTaskTypeSettings}>
                            {t('tasktype.general.modify_types_label')}
                            <FontAwesomeIcon 
                                icon={faWrench} 
                                className='text-link-icon'
                            />
                        </span>
                    )}
                </div>
                <div style={{ height: '34px' }}>
                    <TabSelect<TaskType>
                        options={taskTypes}
                        selectedOption={taskTypes.find(taskType => taskType.id === data.type)}
                        onChange={(selectedOption) => handleTaskTypeChange(selectedOption)}
                        optionFormat='name'
                        showIconOnly={true}
                    />
                </div>
            </div>
            <div className='date-time-row'>
                <div>
                    <label>
                        {t('task.general.start_date_label')}
                    </label>
                    <DateInput
                        id='date'
                        name='date'
                        value={data.date ? new Date(data.date) : undefined}
                        localDatetime={data.start_datetime ?? undefined}
                        onChange={(selectedValue) => handleDateChange(selectedValue, 'date')} 
                        isInvalid={errorMessages['date'] ? true : false}
                    />
                    {errorMessages['date'] && (
                        <div className='error-message'>
                            {t(errorMessages['date'])}
                        </div>
                    )}
                </div>
                <div>
                    <label>
                        {t('task.general.start_time_label')}
                    </label>
                    <TimeInput
                        type='time'
                        id='start_time'
                        name='start_time'
                        value={data.start_time ?? undefined}
                        localDatetime={data.start_datetime ?? undefined}
                        onChange={(selectedTime) => handleTimeChange(selectedTime, 'start_time')} 
                    />
                </div>
                {/* <div>
                    <label>
                        {t('task.general.end_time_label')}
                    </label>
                    <TimeInput
                        type='time'
                        id='end_time'
                        name='end_time'
                        value={data.end_time ?? undefined}
                        localDatetime={data.end_datetime ?? undefined}
                        onChange={(selectedTime) => handleTimeChange(selectedTime, 'end_time')} 
                    />
                </div> */}
                {/* {showEndDateField() && (
                    // Only show the end date field when the task end-time ends on the next day
                    <div>
                        <label>
                            {t('task.general.end_date_label')}
                        </label>
                        <DateInput
                            id='end_date'
                            name='end_date'
                            value={data.end_date ? new Date(data.end_date) : undefined}
                            localDatetime={data.end_datetime ?? undefined}
                            onChange={(selectedValue) => handleDateChange(selectedValue, 'end_date')} 
                        />
                    </div>
                )} */}
            </div>
            <div>
                <label>
                    {t('task.general.note_label')}
                </label>
                <TextareaInput
                    name='note'
                    value={data.note ?? ''}
                    isNote={true}
                    enableStyling='simple'
                    showToolbarInitially={false}
                    rows={1}
                    placeholder='task.general.note_placeholder'
                    onChange={(value) => handleTextareaChange(value, 'note')}                    
                />
                <p className='helper-text'>
                    {t('task.general.note_helperText')}
                </p>
            </div>
            <div>
                <label>
                    {t('task.general.assignee_label')}
                </label>
                <Dropdown<UserType>
                    options={assignees}
                    id='assignee'
                    name='assignee'
                    disabled_selected={t('task.general.assignee_placeholder')}
                    selectedOption={assignees.find(assignee => assignee.id === data.assignee)}
                    onChange={(selectedValue) => handleDropdownChange(selectedValue, 'assignee')}
                    selectionFormat={(option) => `${option.full_name}`}
                    optionFormat='full_name'
                    allowNoneOption={false} 
                    showSearch={true}
                />
            </div>
            <div className='form-information'>
                {data.created_by_user && data.created && (() => {
                    // Find the created by user and format the created date
                    const createdByUser = assignees.find(assignee => assignee.id === data.created_by_user);
                    const createdDate = formatTimelineDate(data.created, false);
                    if (!createdByUser) return null;

                    return (
                        <p>
                            {t('task.general.created_information', { date: createdDate, creator: createdByUser.full_name })}
                        </p>
                    );
                })()}

                {data.modified_by_user && data.modified && (() => {
                    // Find the modified by user and format the created date
                    const modifiedByUser = assignees.find(assignee => assignee.id === data.modified_by_user);
                    const modifiedDate = formatTimelineDate(data.modified, false);
                    if (!modifiedByUser) return null;

                    return (
                        <p>
                            {t('task.general.modified_information', { date: modifiedDate, modifier: modifiedByUser.full_name })}
                        </p>
                    );
                })()}

                {data.checked && data.checked_by_user && data.checked_at && (() => {
                    const user = assignees.find(a => a.id === data.checked_by_user);
                    const checkedDate = formatTimelineDate(data.checked_at, false);
                    if (!user) return null;

                    return (
                        <p>
                            {t('task.general.checked_information', { date: checkedDate, checker: user.full_name })}
                        </p>
                    );
                })()}
            </div>
            <div className='footer'>
                <div className='left-content'>
                    {placement === 'modal' && data.id && (
                        <SecondaryButton
                            onClick={handleDelete} 
                            label="task.general.delete_task_label"
                            size='extra-small'
                            customClass='danger'
                        />
                    )}
                </div>
                <div className='right-content'>
                    <SecondaryButton
                        onClick={handleClose} 
                        label="general.cancel"
                        size={placement === 'inline' ? 'extra-small' : 'small'}
                    />
                    <PrimaryButton
                        onClick={handleSubmit}
                        label="general.save"
                        size={placement === 'inline' ? 'extra-small' : 'small'}
                        loading={buttonLoader}
                    />
                </div>
            </div>
        </div>
    );
};

export default TaskFormFields;